import { useCallback, useState } from 'react'
import { AdminShell } from '@/components/admin/AdminShell'
import { SectionHeader } from '@/components/admin/SectionHeader'
import { OverviewSection } from '@/components/admin/OverviewSection'
import { UsersTab } from '@/components/admin/UsersTab'
import { WordsTab } from '@/components/admin/WordsTab'
import { InvitationCodesTab } from '@/components/admin/InvitationCodesTab'
import { LanguagesTab } from '@/components/admin/LanguagesTab'
import { ReadingsTab } from '@/components/admin/ReadingsTab'
import { SrsTab } from '@/components/admin/SrsTab'
import { BrainTab } from '@/components/admin/BrainTab'
import { SourceBadgesTab } from '@/components/admin/SourceBadgesTab'
import { EnrichmentRunsTab } from '@/components/admin/EnrichmentRunsTab'
import { findAdminSection, isAdminTab } from '@/components/admin/adminSections'
import type { AdminTab } from '@/types/admin'

const SECTION_PARAM = 'section'

/** The section named in the URL, so a reload or a shared link lands where you were. */
function sectionFromUrl(): AdminTab {
  if (typeof window === 'undefined') return 'overview'
  const value = new URLSearchParams(window.location.search).get(SECTION_PARAM)
  return isAdminTab(value) ? value : 'overview'
}

function writeSectionToUrl(key: AdminTab) {
  const url = new URL(window.location.href)
  if (key === 'overview') url.searchParams.delete(SECTION_PARAM)
  else url.searchParams.set(SECTION_PARAM, key)
  window.history.replaceState(window.history.state, '', url)
}

export default function AdminIndex() {
  const [active, setActive] = useState<AdminTab>(sectionFromUrl)
  const [actionSlot, setActionSlot] = useState<HTMLDivElement | null>(null)
  const section = findAdminSection(active)

  const select = useCallback((key: AdminTab) => {
    setActive(key)
    writeSectionToUrl(key)
  }, [])

  return (
    <AdminShell active={active} onSelect={select}>
      <SectionHeader
        title={section.label}
        description={section.description}
        actionSlotRef={setActionSlot}
      />

      <OverviewSection active={active === 'overview'} onNavigate={select} />
      <UsersTab active={active === 'users'} />
      <InvitationCodesTab active={active === 'invitation_codes'} actionSlot={actionSlot} />
      <LanguagesTab active={active === 'languages'} actionSlot={actionSlot} />
      <ReadingsTab active={active === 'readings'} />
      <WordsTab active={active === 'words'} />
      <SrsTab active={active === 'srs'} />
      <BrainTab active={active === 'brain'} />
      <SourceBadgesTab active={active === 'source_badges'} />
      <EnrichmentRunsTab active={active === 'enrichment_runs'} />
    </AdminShell>
  )
}
